import React from 'react';

import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { FormState } from 'final-form';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';

import { useCheckEmailSuppressionsLazyQuery, useDeleteSuppressionFromEmailMutation } from 'src/api';
import { CheckboxField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { FormField } from './constants';
import { FormValues } from './types';
import { formValidationSchema } from './validation';

type ResubscribeEmailModalContentProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
};

export const ResubscribeEmailModalContent: React.FC<ResubscribeEmailModalContentProps> = ({
  onCloseModal,
  switchClosability,
}) => {
  const { createApolloContext } = useNotification();

  const [isEmailSuppressed, setIsEmailSuppressed] = React.useState<boolean | null>(null);
  const currentEmailRef = React.useRef<string | null>(null);

  const [checkEmailSuppressions, { loading: checkEmailSuppressionsLoading }] =
    useCheckEmailSuppressionsLazyQuery({ fetchPolicy: 'no-cache' });

  const [deleteSuppressionFromEmail] = useDeleteSuppressionFromEmailMutation({
    context: createApolloContext({
      errorMessage: 'Failed to resubscribe email',
      successMessage: 'Email successfully resubscribed',
    }),
  });

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      switchClosability(false);

      const { data } = await deleteSuppressionFromEmail({
        variables: {
          email: formValues.email,
        },
      });

      switchClosability(true);
      if (data?.deleteSuppressionFromEmail.success) {
        onCloseModal();
      }
    },
    [deleteSuppressionFromEmail, onCloseModal, switchClosability],
  );

  const handleEmailSearch = React.useCallback(
    async (formValues: FormValues) => {
      setIsEmailSuppressed(null);
      const { data } = await checkEmailSuppressions({
        variables: {
          email: formValues.email,
        },
      });

      if (isNotNil(data?.checkEmailSuppressions)) {
        currentEmailRef.current = formValues.email;
        setIsEmailSuppressed(!data.checkEmailSuppressions.success);
      }
    },
    [checkEmailSuppressions],
  );

  const handleEmailResetOnTyping = React.useCallback(
    (formState: FormState<FormValues>) => {
      if (isNotNil(isEmailSuppressed) && formState.values.email !== currentEmailRef.current) {
        setIsEmailSuppressed(null);
      }
    },
    [isEmailSuppressed],
  );

  return (
    <React.Fragment>
      <Typography variant="body1" color="text.secondary">
        Enter the email address you want to resubscribe. All unsubscribe sources in SendGrid will be
        checked — including global unsubscribes and suppression groups.
      </Typography>

      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        subscription={{ submitting: true }}
        validationSchema={formValidationSchema}
        containerProps={{ mt: 2.5 }}
      >
        {({ submitting }) => (
          <React.Fragment>
            <Box display="flex" gap={2} alignItems="center">
              <Field
                name={FormField.email}
                component={TextField}
                label="Email Address"
                placeholder="Type Email Address"
                required
                fullWidth
              />
              <FormSpy<FormValues> subscription={{ values: true, valid: true }}>
                {({ values, valid }) => (
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={() => handleEmailSearch(values)}
                    loading={checkEmailSuppressionsLoading}
                    disabled={!valid || submitting}
                  >
                    Search
                  </Button>
                )}
              </FormSpy>
            </Box>

            {isNotNil(isEmailSuppressed) && (
              <React.Fragment>
                <Alert
                  severity={isEmailSuppressed ? 'info' : 'neutral'}
                  icon={<InfoIcon />}
                  sx={{ mt: 2 }}
                >
                  {isEmailSuppressed
                    ? 'Email found in unsubscribe sources'
                    : 'Email not found in any unsubscribe sources'}
                </Alert>
                <FormSpy subscription={{ values: true }} onChange={handleEmailResetOnTyping} />
              </React.Fragment>
            )}

            {isEmailSuppressed && (
              <Box mt={2}>
                <Field
                  name={FormField.confirmation}
                  component={CheckboxField}
                  label="I confirm the user explicitly requested to be resubscribed"
                />
              </Box>
            )}

            <Box display="flex" gap={2} mt={4} alignItems="center" justifyContent="center">
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                disabled={submitting}
                onClick={onCloseModal}
              >
                Close
              </Button>

              {isEmailSuppressed && (
                <FormSpy<FormValues> subscription={{ values: true }}>
                  {({ values }) => (
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      loading={submitting}
                      disabled={!values.confirmation}
                    >
                      Resubscribe Email
                    </Button>
                  )}
                </FormSpy>
              )}
            </Box>
          </React.Fragment>
        )}
      </Form>
    </React.Fragment>
  );
};

const initialValues: Partial<FormValues> = {
  email: undefined,
};
